import { useTranslation } from 'react-i18next'
import { Grid, Stack, Typography } from '@mui/material'
import { CardAttribute } from '@/components'
import { EntitySoundRecordsProps, ObservationSoundRecords } from '@/features'
import { renderObservedSpecie } from '@/features/observationLogs/ObservationLogsTable/ObservationLogsTableBody'
import { renderSpecieWithOriginal } from '@/features/observationLogs/utils/renderSpecieWithOriginal'
import { ObservationLog, UserGroups } from '@/types'
import { Dispatch, SetStateAction } from 'react'
import { useAuth } from '@/hooks'

export type ObservationDetailsProps = {
  observationLog: ObservationLog
  setUpdatedObservationLog: Dispatch<SetStateAction<ObservationLog>>
} & Pick<
  EntitySoundRecordsProps,
  'updateSoundRecord' | 'handleCloneObservationLog' | 'cloningInProcess'
>

export const ObservationSpecies = ({
  observationLog,
  setUpdatedObservationLog,
  updateSoundRecord,
  handleCloneObservationLog,
  cloningInProcess,
}: ObservationDetailsProps): JSX.Element => {
  const { t } = useTranslation('observationLogs')
  const auth = useAuth()

  const isClient = auth?.user?.groups?.includes(UserGroups.CLIENT) || false

  return (
    <>
      {observationLog.specie2 ? (
        [1, 2, 3, 4].map((n) => {
          const specie = observationLog[
            `specie${n === 1 ? '' : n}` as keyof ObservationLog
          ] as string | null

          const originalSpecie = observationLog[
            `original_specie${n === 1 ? '' : n}` as keyof ObservationLog
          ] as string | null | undefined

          const amount =
            observationLog[`amount${n === 1 ? '' : n}` as keyof ObservationLog]

          const counting_method =
            observationLog[
              `counting_method${n === 1 ? '' : n}` as keyof ObservationLog
            ]

          const recording_number =
            observationLog[
              `recording_number${n === 1 ? '' : n}` as keyof ObservationLog
            ]

          const specieRecords =
            observationLog.sound_recordings?.filter(
              (r) => r.for_specie_number === n
            ) ?? []

          const isNotConfirmedBySoundAnalyst =
            specieRecords.length > 0 &&
            specieRecords.every(
              (r) =>
                r.analysis_status === 'rejected' && r.specie_identified === ''
            )

          return specie ? (
            <Stack
              borderRadius={2}
              p={4}
              my={2}
              bgcolor="grey.200"
              width="100%"
              key={n}
            >
              <Grid xs={12} item={true} container={true} spacing={4}>
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('observation-log.fieldTitles.foundSpecies')}
                    value={
                      <>
                        {renderSpecieWithOriginal(
                          specie,
                          originalSpecie,
                          isClient
                        )}
                        {isNotConfirmedBySoundAnalyst && (
                          <Typography color="error" variant="body2" mb={1}>
                            {t(
                              'observation-log.fieldTitles.notConfirmedBySoundAnalyst'
                            )}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </Grid>
                {!!amount && (
                  <Grid xs={3} item={true}>
                    <CardAttribute
                      title={t('observation-log.fieldTitles.amount')}
                      value={<>{amount}</>}
                    />
                  </Grid>
                )}
                {!!counting_method && (
                  <Grid xs={3} item={true}>
                    <CardAttribute
                      title={t('observation-log.fieldTitles.countingMethod')}
                      value={<>{counting_method}</>}
                    />
                  </Grid>
                )}
                {!!observationLog.recording_number && (
                  <Grid xs={3} item={true}>
                    <CardAttribute
                      title={t('observation-log.fieldTitles.recordingNumber')}
                      value={<>{recording_number}</>}
                    />
                  </Grid>
                )}
              </Grid>

              {!!observationLog.sound_recordings?.length && (
                <Grid xs={12} item={true} mt={2}>
                  <CardAttribute
                    title={t(
                      'observation-log.fieldTitles.specieNumberSoundRecords',
                      {
                        number: n,
                      }
                    )}
                    value={
                      <ObservationSoundRecords
                        observationLog={observationLog}
                        setUpdatedObservationLog={setUpdatedObservationLog}
                        updateSoundRecord={updateSoundRecord}
                        handleCloneObservationLog={handleCloneObservationLog}
                        cloningInProcess={cloningInProcess}
                        forSpecieNumber={n}
                      />
                    }
                  />
                </Grid>
              )}
            </Stack>
          ) : null
        })
      ) : (
        <Stack borderRadius={2} p={4} my={2} bgcolor="grey.200" width="100%">
          <Grid xs={12} item={true} container={true} spacing={4}>
            <Grid xs={3} item={true}>
              <CardAttribute
                title={t('observation-log.fieldTitles.foundSpecies')}
                value={
                  <>
                    {renderObservedSpecie(
                      renderSpecieWithOriginal(
                        observationLog.specie,
                        observationLog.original_specie,
                        isClient
                      ),
                      renderSpecieWithOriginal(
                        observationLog.specie2,
                        observationLog.original_specie2,
                        isClient
                      ),
                      renderSpecieWithOriginal(
                        observationLog.specie3,
                        observationLog.original_specie3,
                        isClient
                      ),
                      renderSpecieWithOriginal(
                        observationLog.specie4,
                        observationLog.original_specie4,
                        isClient
                      ),
                      observationLog.other_specie,
                      observationLog.unknown_specie
                    )}
                    {(() => {
                      const allRecords = observationLog.sound_recordings ?? []
                      const isNotConfirmed =
                        allRecords.length > 0 &&
                        allRecords.every(
                          (r) =>
                            r.analysis_status === 'rejected' &&
                            r.specie_identified === ''
                        )

                      return isNotConfirmed ? (
                        <Typography color="error" variant="body2" mb={1}>
                          {t(
                            'observation-log.fieldTitles.notConfirmedBySoundAnalyst'
                          )}
                        </Typography>
                      ) : null
                    })()}
                  </>
                }
              />
            </Grid>
            {!!observationLog.amount && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.amount')}
                  value={observationLog.amount}
                />
              </Grid>
            )}
            {!!observationLog.counting_method && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.countingMethod')}
                  value={observationLog.counting_method}
                />
              </Grid>
            )}
            {!!observationLog.recording_number && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.recordingNumber')}
                  value={observationLog.recording_number}
                />
              </Grid>
            )}
          </Grid>
          {!!observationLog.sound_recordings?.length && (
            <Grid xs={12} item={true} mt={2}>
              <CardAttribute
                title={t('observation-log.fieldTitles.specieSoundRecords')}
                value={
                  <ObservationSoundRecords
                    observationLog={observationLog}
                    setUpdatedObservationLog={setUpdatedObservationLog}
                    updateSoundRecord={updateSoundRecord}
                    handleCloneObservationLog={handleCloneObservationLog}
                    cloningInProcess={cloningInProcess}
                  />
                }
              />
            </Grid>
          )}
        </Stack>
      )}
    </>
  )
}
